import reactGA from "react-ga4";
import { GA4_ENV, VALID_ROUTES } from "../constants/analytics";
import { ALLOW_PERFORMANCE_COOKIES } from "../constants/storage";
import {
  GA4EventName,
  GA4EventParams,
  GA4UserPropertyKey,
} from "../types/analytics";
import { IS_LOCALHOST } from "../constants/environment";

/** Page performance tracking interface */
export interface PagePerformanceData {
  path: string;
  loadTime: number;
  renderTime: number;
  timestamp: number;
}

/**
 * GoogleAnalyticsV2 - Refactored GA4 analytics utility
 *
 * This class provides proper GA4 event tracking with structured parameters,
 * replacing the legacy Universal Analytics model (category + action) with
 * the GA4 model (event_name + parameters).
 *
 * Key improvements over GoogleAnalytics (V1):
 * - Events use descriptive names instead of category + action
 * - All contextual data is sent as structured parameters (not concatenated strings)
 * - User properties are set for audience segmentation
 * - Web vitals include metric name, value, and rating as parameters
 * - Page load timing includes load_time_ms and render_time_ms as parameters
 * - Debug mode with console logging for development
 * - Performance data tracking and storage
 *
 */
export default class GoogleAnalyticsV2 {
  public static isInitialized: boolean = false;
  private static pageLoadStartTime: number = 0;
  private static readonly pagePerformanceData: PagePerformanceData[] = [];
  private static debugMode: boolean = false;

  /** Make the constructor private to block instantiation outside of the class. */
  private constructor() {}

  /**
   * Initialize GA4 tracking with environment-specific measurement ID.
   * Also sets default user properties if available.
   *
   * @param host - The current hostname used to look up the GA4 measurement ID
   */
  public static initialize(host: string): void {
    const cookieExpiry = 31536000; // override default 2 yrs cookie expiry to just 1 yr
    const isGoogleCookiesAllowed =
      localStorage.getItem(ALLOW_PERFORMANCE_COOKIES) === "true";

    if (!isGoogleCookiesAllowed || this.isInitialized) return;

    reactGA.initialize(GA4_ENV[host], {
      gaOptions: {
        cookieExpires: cookieExpiry,
      },
      gtagOptions: {
        send_page_view: false,
      },
    });

    this.isInitialized = true;
  }

  /**
   * Send a page view event to GA4.
   * Should only be called from AnalyticsTracker to avoid duplicate page views.
   *
   * @param page - The page path (e.g., "/my-data")
   * @param title - Optional page title
   */
  public static sendPageView(page: string, title?: string): void {
    if (!this.isInitialized) return;

    // Catch successful initial login
    if (globalThis.location.search === "?success=1") {
      reactGA.send({ hitType: "pageview", page: "/login-success" });
    }

    if (VALID_ROUTES.includes(page)) {
      reactGA.send({ hitType: "pageview", page, title: title || page });
    }
  }

  /**
   * Track a GA4 event with structured parameters.
   *
   * This is the core event tracking method. Unlike the old triggerEvent()
   * which only accepted category + action, this method accepts any GA4 event
   * name and a parameters object with structured data.
   *
   * @param eventName - The GA4 event name (use GA4EventName enum)
   * @param params - Event parameters as key-value pairs
   *
   * @example
   * GoogleAnalyticsV2.trackEvent(GA4EventName.HeaderClick, {
   *   action: "my-data",
   *   country_code: "US",
   *   source: "gnb",
   * });
   */
  public static trackEvent(
    eventName: GA4EventName | string,
    params: GA4EventParams = {}
  ): void {
    if (!this.isInitialized) return;
    // Debug logging for local development
    if (IS_LOCALHOST) {
      console.log(`[GA4] ${eventName}`, params);
    }
    reactGA.event({
      category: eventName,
      action: eventName,
      ...params,
    });
  }

  /**
   * Track a page load performance event with timing data.
   *
   * @param path - The page path that was loaded
   * @param loadTime - Page load time in milliseconds
   * @param renderTime - Optional render time in milliseconds
   */
  public static trackPageLoad(
    path: string,
    loadTime: number,
    renderTime?: number
  ): void {
    if (!this.isInitialized) return;

    this.trackEvent(GA4EventName.PageLoad, {
      page_path: path,
      load_time_ms: Math.round(loadTime),
      render_time_ms: renderTime ? Math.round(renderTime) : undefined,
    });
  }

  /**
   * Track a web vital metric event.
   *
   * @param metricName - The web vital metric name (e.g., "CLS", "FID", "LCP")
   * @param value - The metric value
   * @param rating - The metric rating ("good", "needs-improvement", "poor")
   */
  public static trackWebVital(
    metricName: string,
    value: number,
    rating: string
  ): void {
    if (!this.isInitialized) return;

    this.trackEvent(GA4EventName.WebVital, {
      metric_name: metricName,
      metric_value: Math.round(value),
      metric_rating: rating,
    });
  }

  /**
   * Set a user property in GA4 for audience segmentation.
   *
   * @param key - The user property key (use GA4UserPropertyKey enum)
   * @param value - The user property value
   *
   * @example
   * GoogleAnalyticsV2.setUserProperty(GA4UserPropertyKey.CountryCode, "US");
   */
  public static setUserProperty(
    key: GA4UserPropertyKey | string,
    value: string | number | boolean
  ): void {
    if (!this.isInitialized) return;

    reactGA.set({ [key]: value });
  }

  /**
   * Set multiple user properties at once.
   *
   * @param properties - Object with user property key-value pairs
   *
   * @example
   * GoogleAnalyticsV2.setUserProperties({
   *   country_code: "US",
   *   has_local_site: true,
   *   login_type: "sa",
   * });
   */
  public static setUserProperties(
    properties: Record<string, string | number | boolean>
  ): void {
    if (!this.isInitialized) return;

    reactGA.set(properties);
  }

  /**
   * Check if debug mode is enabled via URL params, localStorage, or dev environment.
   */
  private static checkDebugMode(): boolean {
    if (globalThis.window === undefined) return false;

    const urlParams = new URLSearchParams(globalThis.location.search);
    if (urlParams.has("debug_mode") || urlParams.has("debug")) {
      return true;
    }

    const localStorageDebug = localStorage.getItem("ga_debug_mode");
    if (localStorageDebug === "true") {
      return true;
    }

    if (process.env.NODE_ENV === "development") {
      return true;
    }

    return false;
  }

  /**
   * Enable or disable debug mode for analytics logging.
   *
   * @param enabled - Whether to enable debug mode
   */
  public static setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
    if (globalThis.window !== undefined) {
      localStorage.setItem("ga_debug_mode", enabled.toString());
    }
    console.log(`🔍 Analytics debug mode ${enabled ? "ENABLED" : "DISABLED"}`);
  }

  /**
   * Track a page view with loading time metrics.
   * Sends both a page_view and page_load event to GA4.
   *
   * @param path - The page path
   * @param loadTime - Page load time in milliseconds
   * @param renderTime - Page render time in milliseconds
   */
  public static trackPageView(
    path: string,
    loadTime?: number,
    renderTime?: number
  ): void {
    const performanceData: PagePerformanceData = {
      path,
      loadTime: loadTime || 0,
      renderTime: renderTime || 0,
      timestamp: Date.now(),
    };

    this.pagePerformanceData.push(performanceData);

    this.sendPageView(path);

    if (loadTime) {
      this.trackPageLoad(path, loadTime, renderTime);
    }

    if (this.debugMode || this.checkDebugMode()) {
      console.log(`📊 ANALYTICS: Page View Tracked`);
      console.log(`📍 Path: ${path}`);
      console.log(`⏱️  Load Time: ${Math.round(loadTime || 0)}ms`);
      console.log(`🎨 Render Time: ${Math.round(renderTime || 0)}ms`);
    }
  }

  /**
   * Start timing page load.
   */
  public static startPageLoadTiming(): void {
    this.pageLoadStartTime =
      globalThis.window !== undefined && globalThis.performance !== undefined
        ? globalThis.performance.now()
        : Date.now();
  }

  /**
   * End timing page load and track the page view.
   *
   * @param path - The page path
   * @param renderTime - Optional render time in milliseconds
   */
  public static endPageLoadTimingAndTrack(
    path: string,
    renderTime?: number
  ): void {
    if (this.pageLoadStartTime === 0) {
      console.warn(
        "Page load timing not started. Call startPageLoadTiming() first."
      );
      return;
    }

    const loadTime =
      globalThis.window !== undefined && globalThis.performance !== undefined
        ? globalThis.performance.now() - this.pageLoadStartTime
        : 0;
    this.trackPageView(path, loadTime, renderTime);
    this.pageLoadStartTime = 0;
  }

  /**
   * Get performance metrics for all tracked pages.
   */
  public static getPerformanceData(): PagePerformanceData[] {
    return [...this.pagePerformanceData];
  }

  /**
   * Get average loading time for a specific path.
   */
  public static getAverageLoadTime(path: string): number {
    const pathData = this.pagePerformanceData.filter(
      (data) => data.path === path
    );
    if (pathData.length === 0) return 0;

    const totalLoadTime = pathData.reduce(
      (sum, data) => sum + data.loadTime,
      0
    );
    return totalLoadTime / pathData.length;
  }

  /**
   * Disable GA4 tracking and clean up global references.
   */
  public static disableTracking(): void {
    delete window.GoogleAnalyticsObject;
    delete window.ga;
    delete window.gaData;
    delete window.gaGlobal;
    delete window.gaplugins;
    delete window.google_tag_data;

    this.isInitialized = false;
  }
}
